import { getCatalogByType } from "./catalog";
import {
	createConnector as createConnectorApi,
	deleteConnector as deleteConnectorApi,
	exportSyncJobs as exportSyncJobsApi,
	getConnector as getConnectorApi,
	getIntegrationsCatalog,
	listConnectors as listConnectorsApi,
	listSyncJobs as listSyncJobsApi,
	patchConnectorEnabled as patchEnabledApi,
	syncConnector as syncConnectorApi,
	testConnector as testConnectorApi,
	updateConnector as updateConnectorApi
} from "../../api/integrations.api";

const TYPE_TO_API = {
	Salesforce: "salesforce",
	HubSpot: "hubspot",
	SAP: "sap",
	SharePoint: "sharepoint",
	ServiceNow: "servicenow",
	"Microsoft Dynamics": "dynamics"
};

const TYPE_TO_UI = {
	salesforce: "Salesforce",
	hubspot: "HubSpot",
	sap: "SAP",
	sharepoint: "SharePoint",
	servicenow: "ServiceNow",
	dynamics: "Microsoft Dynamics",
	microsoft_dynamics: "Microsoft Dynamics",
	"microsoft dynamics": "Microsoft Dynamics"
};

const FREQ_TO_API = {
	Manual: "manual",
	Hourly: "hourly",
	Daily: "daily",
	Weekly: "weekly"
};

const FREQ_TO_UI = {
	manual: "Manual",
	hourly: "Hourly",
	daily: "Daily",
	weekly: "Weekly"
};

const CONFIG_KEY_TO_API = {
	instanceUrl: "instance_url",
	clientId: "client_id",
	clientSecret: "client_secret",
	hubId: "hub_id",
	baseUrl: "base_url",
	entitySet: "entity_set",
	authType: "auth_type",
	username: "username",
	password: "password"
};

const CONFIG_KEY_TO_UI = Object.fromEntries(
	Object.entries(CONFIG_KEY_TO_API).map(([ui, api]) => [api, ui])
);

function unwrap(raw) {
	if (raw == null) return raw;
	if (raw && typeof raw === "object" && raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
		if (raw.success != null || raw.data.providers || raw.data.connectors || raw.data.jobs || raw.data.id) {
			return raw.data;
		}
	}
	return raw;
}

export function getApiErrorMessage(error, fallback = "Something went wrong. Please try again.") {
	const status = error?.response?.status;
	if (
		error?.code === "ERR_CANCELED" ||
		error?.name === "CanceledError" ||
		error?.name === "AbortError"
	) {
		return fallback;
	}
	if (status === 504) {
		return "Gateway timeout (504). The integration server did not respond in time. Enable and delete are failing on the backend/CloudFront, not in the UI.";
	}
	if (status === 502 || status === 503) {
		return "The integration service is unavailable. Try again in a moment.";
	}
	if (error?.code === "ECONNABORTED" || /timeout/i.test(String(error?.message || ""))) {
		return "The request timed out. The integration server did not respond in time.";
	}

	const data = error?.response?.data;
	let parsed = data;
	if (typeof Blob !== "undefined" && data instanceof Blob) {
		return fallback;
	}
	if (typeof data === "string" && data.trim()) {
		if (/<\s*(html|body|head)[\s>]/i.test(data) || /Gateway Timeout|cloudfront/i.test(data)) {
			return status === 504
				? "Gateway timeout (504). The integration server did not respond in time."
				: fallback;
		}
		try {
			parsed = JSON.parse(data);
		} catch {
			return data.length > 180 ? fallback : data;
		}
	}
	if (parsed?.message) return String(parsed.message);
	if (parsed?.error?.message) return String(parsed.error.message);
	if (typeof parsed?.error === "string") return parsed.error;
	if (typeof parsed?.detail === "string") return parsed.detail;
	if (Array.isArray(parsed?.detail)) {
		const msgs = parsed.detail.map((d) => d?.msg || d?.message).filter(Boolean);
		if (msgs.length) return msgs.join(". ");
	}
	return error?.message || fallback;
}

export function toApiType(type) {
	if (TYPE_TO_API[type]) return TYPE_TO_API[type];
	return String(type || "")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "_");
}

export function toUiType(type) {
	const key = String(type || "")
		.trim()
		.toLowerCase()
		.replace(/\s+/g, "_");
	return TYPE_TO_UI[key] || TYPE_TO_UI[String(type || "").trim().toLowerCase()] || type || "";
}

export function toApiFrequency(frequency) {
	return FREQ_TO_API[frequency] || String(frequency || "manual").toLowerCase();
}

export function toUiFrequency(frequency) {
	const key = String(frequency || "").toLowerCase();
	if (!key) return "";
	return FREQ_TO_UI[key] || frequency || "";
}

function titleHealth(value) {
	if (value == null || value === "") return "—";
	const key = String(value || "").toLowerCase();
	if (key === "healthy") return "Healthy";
	if (key === "failed" || key === "error") return "Failed";
	if (key === "warning" || key === "degraded") return "Warning";
	return value;
}

function toEpoch(value) {
	if (value == null || value === "") return null;
	if (typeof value === "number" && Number.isFinite(value)) return value;
	const ms = Date.parse(String(value));
	return Number.isNaN(ms) ? null : ms;
}

function isMaskedSecret(value) {
	const s = String(value ?? "").trim();
	return !s || /^•+$/.test(s);
}

export function toApiConfig(values = {}, { omitMasked = true } = {}) {
	const out = {};
	Object.entries(values || {}).forEach(([key, value]) => {
		if (value == null) return;
		const apiKey = CONFIG_KEY_TO_API[key] || key;
		const str = typeof value === "string" ? value.trim() : value;
		if (omitMasked && (key === "clientSecret" || key === "password" || apiKey === "client_secret") && isMaskedSecret(str)) {
			return;
		}
		if (str === "") return;
		out[apiKey] = str;
	});
	return out;
}

export function toUiConfig(config = {}) {
	const src = config && typeof config === "object" ? config : {};
	const out = {};
	Object.entries(src).forEach(([key, value]) => {
		if (key === "has_client_secret" || key === "has_password" || key.endsWith("_masked")) return;
		const uiKey = CONFIG_KEY_TO_UI[key] || key;
		if (value == null) return;
		if (typeof value === "string" && /secret|password/i.test(key) && isMaskedSecret(value)) {
			out[uiKey] = "";
			return;
		}
		out[uiKey] = value;
	});
	if (src.client_secret_masked && out.clientSecret == null) out.clientSecret = "";
	if (src.password_masked && out.password == null) out.password = "";
	return out;
}

export function normalizeConnector(raw) {
	if (!raw || typeof raw !== "object") return null;
	const src = raw.connector && typeof raw.connector === "object" ? raw.connector : raw;
	const nextSyncAt = src.next_sync_at || src.nextSyncAt || null;
	const nextMs = toEpoch(nextSyncAt);
	const enabled = src.enabled !== false;
	const connectionStatus = src.connection_status || src.connectionStatus || (enabled ? "" : "Disabled");
	return {
		id: String(src.id || src.connector_id || ""),
		name: src.name || toUiType(src.type) || "",
		type: toUiType(src.type),
		description: src.description || "",
		connectionStatus: !enabled && (!connectionStatus || connectionStatus === "Connected") ? "Disabled" : connectionStatus || "—",
		health: titleHealth(src.health),
		enabled,
		frequency: toUiFrequency(src.sync_frequency || src.frequency),
		authMethod: src.auth_method || src.authMethod || toUiConfig(src.config || {}).authType || "",
		version: src.version || "",
		createdBy: src.created_by || src.createdBy || "",
		createdOn: src.created_at || src.createdOn || "",
		createdAt: src.created_at || src.createdAt || null,
		lastSync: toEpoch(src.last_sync_at || src.lastSyncAt || src.last_sync),
		nextSyncAt,
		nextSyncInMs: nextMs ? Math.max(0, nextMs - Date.now()) : null,
		uptime: src.uptime_30d || src.uptime || "",
		avgLatency: src.avg_latency || src.avgLatency || "",
		latestError: src.latest_error || src.latestError || null,
		lastIssue: src.last_issue || src.lastIssue || src.latest_error || null,
		config: toUiConfig(src.config || {}),
		consecutiveFailures: Number(src.consecutive_failures ?? 0)
	};
}

function extractList(raw, keys) {
	const data = unwrap(raw);
	if (Array.isArray(data)) return data;
	for (const key of keys) {
		if (Array.isArray(data?.[key])) return data[key];
	}
	if (Array.isArray(data?.items)) return data.items;
	return [];
}

export function mapCatalogProviders(providers) {
	return (Array.isArray(providers) ? providers : [])
		.map((p, index) => {
			const typeUi = toUiType(p?.connector_type || p?.type || p?.provider);
			const id = String(p?.id || p?.provider || p?.connector_type || typeUi || `provider-${index}`)
				.trim()
				.toLowerCase()
				.replace(/\s+/g, "_");
			const schema = getCatalogByType(typeUi || id);
			const available =
				p?.available !== false &&
				!/coming[\s_-]*soon/i.test(String(p?.certification_status || p?.status || ""));
			const status = available ? "available" : "coming-soon";
			const apiFields = Array.isArray(p?.fields) ? p.fields : [];
			return {
				id: id || `provider-${index}`,
				name: p?.display_name || p?.name || typeUi || "Connector",
				type: typeUi || p?.display_name || id,
				authLabel:
					Array.isArray(p?.auth_methods) && p.auth_methods.length
						? p.auth_methods.join(" · ")
						: p?.auth_label || schema?.authLabel || "",
				authMethod: p?.auth_method || p?.auth_methods?.[0] || schema?.authMethod || "",
				description: p?.notes || p?.description || "",
				instruction: schema?.instruction || "",
				fields: apiFields.length ? apiFields : schema?.fields || [],
				testSummaryKeys: schema?.testSummaryKeys || [],
				initials: p?.initials,
				status,
				section: status,
			};
		})
		.filter((item) => item.id);
}

export async function fetchCatalog() {
	const raw = await getIntegrationsCatalog();
	const data = unwrap(raw);
	return mapCatalogProviders(extractList(data, ["providers", "items", "catalog"]));
}

export async function fetchConnectors(params = {}) {
	const raw = await listConnectorsApi(params);
	const data = unwrap(raw);
	const items = extractList(data, ["connectors"]).map(normalizeConnector).filter((c) => c?.id);
	return {
		items,
		total: Number(data?.total ?? items.length)
	};
}

export async function fetchConnector(id) {
	const raw = await getConnectorApi(id);
	const data = unwrap(raw);
	const src = data?.connector || data;
	return normalizeConnector(src);
}

export async function saveConnector({ existingId, name, type, description, frequency, config, enabled = true }) {
	const body = {
		name,
		description: description || "",
		config: toApiConfig(config),
		sync_frequency: toApiFrequency(frequency),
		enabled
	};
	if (existingId) {
		const raw = await updateConnectorApi(existingId, body);
		return normalizeConnector(unwrap(raw)) || { id: existingId, name };
	}
	const raw = await createConnectorApi({ ...body, type: toApiType(type) });
	const data = unwrap(raw);
	return normalizeConnector(data) || normalizeConnector(data?.connector) || { id: data?.id, name };
}

export async function setConnectorEnabled(id, enabled) {
	const raw = await patchEnabledApi(id, enabled);
	const data = unwrap(raw);
	if (data && typeof data === "object" && data.id) return normalizeConnector(data);
	return { id, enabled, connectionStatus: enabled ? "Connected" : "Disabled" };
}

export async function removeConnector(id) {
	await deleteConnectorApi(id);
	return { id, deleted: true };
}

export async function runConnectorTest({ connectorId, type, config }) {
	const body = {
		type: toApiType(type),
		config: toApiConfig(config, { omitMasked: true })
	};
	if (connectorId) body.connector_id = connectorId;
	const raw = await testConnectorApi(body);
	const data = unwrap(raw) || {};
	const failed =
		data.success === false ||
		String(data.result || "").toLowerCase() === "failed" ||
		String(data.auth_status || "").toLowerCase() === "rejected";
	const ok =
		!failed &&
		(data.success === true ||
			String(data.result || "").toLowerCase() === "success" ||
			String(data.auth_status || "").toLowerCase() === "valid" ||
			String(data.connection_status || "").toLowerCase() === "connected" ||
			String(data.health || "").toLowerCase() === "healthy");
	return {
		ok,
		result: ok ? "Success" : "Failed",
		authStatus: ok ? "Valid" : "Rejected",
		latencyMs: Number(data.latency_ms ?? data.latencyMs ?? 0),
		message: data.message || (ok ? "Connection successful" : "Connection failed"),
		errorCode: data.error_code || data.errorCode || null,
		details: data.details || {}
	};
}

export async function startConnectorSync(id) {
	const raw = await syncConnectorApi(id);
	return unwrap(raw) || {};
}

function mapJobStatus(status) {
	const key = String(status || "").toLowerCase();
	if (["success", "succeeded", "completed", "complete"].includes(key)) return "Success";
	if (["failed", "error", "cancelled", "canceled"].includes(key)) return "Failed";
	if (["queued", "running", "in_progress", "accepted", "pending"].includes(key)) return "Running";
	return status || "Running";
}

function formatDuration(job) {
	if (job.duration) return String(job.duration);
	const seconds = job.duration_seconds ?? job.durationSeconds;
	if (seconds == null || seconds === "") return "—";
	const s = Math.max(0, Math.round(Number(seconds)));
	if (!Number.isFinite(s)) return "—";
	if (s < 60) return `${s}s`;
	const m = Math.floor(s / 60);
	const rem = s % 60;
	return `${m}m ${String(rem).padStart(2, "0")}s`;
}

export function normalizeSyncJob(job) {
	if (!job || typeof job !== "object") return null;
	return {
		id: String(job.id || job.job_id || ""),
		connectorId: job.connector_id || job.connectorId || "",
		connector: job.connector_name || job.connectorName || job.connector || "—",
		started: job.started_at || job.startedAt || "",
		finished: job.finished_at || job.finishedAt || "",
		duration: formatDuration(job),
		documents: Number(job.documents_processed ?? job.documentsProcessed ?? job.documents ?? 0),
		status: mapJobStatus(job.status),
		error: job.error || job.error_code || "",
		triggeredBy: job.triggered_by || job.triggeredBy || "—"
	};
}

export async function fetchSyncJobs(params = {}) {
	const raw = await listSyncJobsApi(params);
	const data = unwrap(raw);
	const jobs = extractList(data, ["jobs", "items"]).map(normalizeSyncJob).filter((j) => j?.id);
	return {
		items: jobs,
		total: Number(data?.total ?? data?.count ?? jobs.length),
		page: Number(data?.page ?? params.page ?? 1),
		pageSize: Number(data?.page_size ?? params.page_size ?? jobs.length)
	};
}

export async function fetchSyncsTodayCount() {
	const res = await fetchSyncJobs({ range: 1, page: 1, page_size: 100 });
	return Number(res.total ?? res.items.length ?? 0);
}

function downloadBlob(blob, filename) {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	URL.revokeObjectURL(url);
}

function filenameFromDisposition(header) {
	const raw = String(header || "");
	const match = raw.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
	return match ? decodeURIComponent(match[1].trim()) : "sync-history.csv";
}

function looksLikeCsv(text) {
	const first = String(text || "").trim().split(/\r?\n/)[0] || "";
	return first.includes(",") || first.toLowerCase().includes("connector");
}

/** Downloads the CSV file returned by GET /integrations/sync-jobs/export. Never builds CSV on the client. */
export async function downloadSyncJobsCsv(params = {}) {
	const res = await exportSyncJobsApi(params);
	const blob = res.data;
	const type = String(res.headers?.["content-type"] || "").toLowerCase();
	const filename = filenameFromDisposition(res.headers?.["content-disposition"]);

	if (type.includes("application/json")) {
		const text = await blob.text();
		let json;
		try {
			json = JSON.parse(text);
		} catch {
			if (!looksLikeCsv(text)) {
				throw new Error("Export did not return a CSV file from the backend.");
			}
			downloadBlob(new Blob([text], { type: "text/csv" }), filename);
			return;
		}

		const fileUrl = json?.download_url || json?.url;
		if (typeof fileUrl === "string" && fileUrl) {
			window.location.assign(fileUrl);
			return;
		}

		const csvText =
			typeof json === "string"
				? json
				: typeof json?.csv === "string"
				? json.csv
				: typeof json?.content === "string"
				? json.content
				: null;

		if (!csvText) {
			throw new Error("Export did not return a CSV file from the backend.");
		}
		downloadBlob(new Blob([csvText], { type: "text/csv" }), filename);
		return;
	}

	downloadBlob(blob, filename);
}
